import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DeviceFactory } from 'src/app/home/site-dashboard/device/device-factory/device-factory';
import { DeviceService } from 'src/app/home/site-dashboard/device/device.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';

@Component({
	selector: 'app-truck-utilization-charts',
	templateUrl: './truck-utilization-charts.component.html',
	styleUrls: ['./truck-utilization-charts.component.css']
})

export class TruckUtilizationChartsComponent implements OnInit{
	@ViewChild("popupModal") popupModal: ModalComponent;

	@Input() title: any = {};
	@Input() devices: any = [];
	@Input() data: any = [];
	@Input() toDrawValue: string = 'score';
	@Input() maxValue: number = 100;
	@Input() deviceType: number = DeviceFactory.types.iotah;
	@Input() selectedPeriod: string = 'last_week';
	@Input() xAxisColumnLabel: string = '% of Total Trucks';
	@Input() modalDeviceType: string = 'trucks';

	siteID: number = 0;
	customerID: number = 0;
	selectedRangeTitle: string = '';

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		fileName: this.title || '',
		hasPagination: true,
		pageSize: 100,
		csvFileHeaderInfo: []
	};

	columnConfig: ColumnsConfig[] = [
		{ key: 'serial_number', name: this.translateService.instant('g.serial_number'), type: "link"},
		{ key: 'device_id', name: this.deviceService.translate('g.device_id', {sct_device_type: this.deviceType, is_study_device: false}), type: "string" },
		{ key: 'value', name: this.translateService.instant(`g.${this.toDrawValue}`), type: "number"},
	];

	chartData = [];
	ranges = [];
	columnNames = [
		'Score Range',
		this.xAxisColumnLabel,
		{ role: 'annotation' },
		{ role: 'tooltip', type: 'string', p: { html: true } }
	];	
	config = {};
	siteSub: Subscription = new Subscription();
	siteInfo: any = {};

	constructor(
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private deviceService: DeviceService,
		private commonService: CommonService,
		private sideMenuService: SideMenuService
	) {}

	ngOnInit() {
		this.chartData = [];
		this.ranges = [];
		this.siteID = this.route.parent.snapshot.params.siteId || 0;
		this.customerID = this.route.parent.snapshot.params.customerId || 0;
		this.tableData = [];
		this.tableConfig.fileName = (this.title || '') + `_${this.selectedPeriod}`;
		
		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo) 
				this.siteInfo = siteInfo;
			
			this.prepareConfig();
			this.prepareTableColumns();
			this.prepareChartRanges();
			this.prepareChartData();
		});
	}

	ngOnChanges() {
		this.chartData = [];
		this.ranges = [];
		this.tableData = [];
		this.tableConfig.fileName = (this.title || '') + `_${this.selectedPeriod}`;
		
		this.prepareConfig();
		this.prepareTableColumns();
		this.prepareChartRanges();
		this.prepareChartData();
	}

	prepareTableColumns() {
		this.columnConfig = [
			{ key: 'serial_number', name: this.translateService.instant('g.serial_number'), type: "link" },
			{ key: 'device_id', name: this.deviceService.translate('devices.truck_id', {sct_device_type: this.deviceType, is_study_device: false}), type: "string" },
			{ key: 'value', name: this.title, type: "number" },
		];
	}

	prepareChartData() {
		if (!this.data || !this.data.length) {
			this.chartData = [];
			return;
		}

		const total = this.data.length;

		this.ranges.forEach(r => r.devices = []);

		for (const d of this.data) {
			let value = Math.floor(d[this.toDrawValue] || 0);

			if (value > this.maxValue) {
				value = this.maxValue;
			}

			const range = this.ranges.find(r => value >= r.from && value <= r.to);

			if (range) 
				range.devices.push(d.macAddress || d.mac_address);
		}

		this.chartData = this.ranges.map(r => {
			const count = r.devices.length;
			const percent = total ? Math.round((count / total) * 100) : 0;

			const tooltip = `
				<div style="padding:8px; white-space: nowrap;">
					<div><strong>Count of ${this.modalDeviceType}: ${count}</strong></div>
					<div><strong>Total count of ${this.modalDeviceType}: ${total}</strong></div>
				</div>
			`;

			return [
				r.label,
				percent,
				percent,
				tooltip
			];
		});
	}

	prepareChartRanges() {
		const ranges = [];
		for (let i = 0; i < this.maxValue; i = i + 10) {
			let from = i ? i + 1 : 0;
			const isLastRange = i + 10 >= this.maxValue;

			ranges.push({
				devices: [],
				from,
				to: i + 10,
				label: isLastRange ? `${from}% and above` : `${from} - ${i + 10}%`,
			});
		}

		this.ranges = ranges;
	}

	onSelectRange(event: any) {
		if (!event || !('row' in event))
			return;

		this.tableData = [];
		const groupIndex = event.row;
		const selectedGroup = this.ranges[groupIndex] || null;

		if (!selectedGroup)
			return;

		const selectedDevices = selectedGroup.devices;

		for (let macAddress in this.devices) {
			if (!selectedDevices.includes(macAddress))
				continue;

			const deviceData = this.data.find(d => d.macAddress === macAddress || d.mac_address === macAddress) || {};
			const device = this.devices[macAddress] || {};
			const value = deviceData[this.toDrawValue] || 0;
			this.tableData.push({
				serial_number: { value: device.serial_number, link: ['/', this.customerID, this.siteID, device.mac_address || macAddress, 'performance'] },
				device_id: { value: device.truck_id },
				value: { value: value },
			});
		}
		if (!this.tableData.length)
			return;

		this.selectedRangeTitle = selectedGroup.label;
		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			includeSpacing: false
		});
		this.popupModal.show();
	}

	ngOnDestroy() {
		if (this.popupModal)
			this.popupModal.hide();
	}

	prepareConfig() {
		this.config = {
			legend: { position: 'none' },

			hAxis: {
				title: this.xAxisColumnLabel,
				minValue: 0,
				maxValue: 100,
				format: '#\'%\''
			},

			tooltip: {
				isHtml: true
			},
			vAxis: {
				title: 'Score Range'
			},

			annotations: {
				alwaysOutside: false,
				textStyle: {
					fontSize: 12,
					color: '#070707'
				}
			},

			bar: { groupWidth: '70%' },
			colors: ['#8bcbe5'],

			chartArea: {
				left: 100,
				right: 30,
				top: 40,
				bottom: 50,
				width: '900px' 
			}
		}
	}
}
